import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from "../../shared/ui-common/button/button.component";
import { SignUp } from '../data/models/sign-up.model';
import { User } from '../data/models/user.model';
import { AuthLayoutComponent } from "../ui/auth-layout/auth-layout.component";
import { PinCodeComponent } from "../ui/pin-code/pin-code.component";
import { pincodeValidator } from '../util/auth.util';
import { AuthService } from './../data/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe } from 'src/app/shared/data/pipes/translate.pipe';
import { lastValueFrom } from 'rxjs';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { TranslationService } from 'src/app/shared/data/translation.service';
import { PhoneInputComponent } from 'src/app/shared/ui-common/phone-input/phone-input.component';
import { EnvironmentService } from 'src/app/shared/data/environment.service';

enum Step {
  Welcome = 'welcome',
  Pincode = 'pincode',
  ConfirmPincode = 'confirm-pincode',
  Consent = 'consent',
  PersonalDetails = 'personal-details',
  SsoLinking = 'sso-linking',
  Done = 'done'
}

@Component({
  selector: 'app-feature-sign-up',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AuthLayoutComponent,
    ButtonComponent,
    PinCodeComponent,
    TranslatePipe,
    PhoneInputComponent
  ],
  templateUrl: './feature-sign-up.component.html',
  styleUrls: ['./feature-sign-up.component.scss', '../ui/auth.styles.scss']
})
export class FeatureSignUpComponent implements OnInit {
  signUpForm: FormGroup;
  signUpData!: SignUp;
  user!: User;

  dropdownOpen = false;
  steps = Step;
  isTransitioning = false;
  step = signal<Step>(Step.Welcome);
  pincodeNotMatched = signal<boolean>(false);
  signUpValidationError = signal<string>('');
  companyName = signal<string>('');

  get errorMessage(): string {
    if (this.signUpForm.controls['pincode'].errors && this.signUpForm.controls['pincode'].touched) {
      if (this.signUpForm.controls['pincode'].errors['invalidPincode']) {
        return 'WebApp.v1.Auth.ForgotCode.Error.Not4Digit';
      } else if (this.signUpForm.controls['pincode'].errors['repeatingDigits']) {
        return 'WebApp.v1.Auth.ForgotCode.Error.RepeatingDigits';
      } else if (this.signUpForm.controls['pincode'].errors['sequentialDigits']) {
        return 'WebApp.v1.Auth.ForgotCode.Error.NoSequentialDigits';
      }
    } else if (this.pincodeNotMatched()) {
      return 'WebApp.v1.Auth.ForgotCode.Error.NotMatched';
    }

    return '';
  }

  get pincodeControl(): AbstractControl<string> | null {
    return this.signUpForm.get('pincode');
  }

  get firstname(): string {
    return this.user?.firstname;
  }

  get lastname(): string {
    return this.user?.lastname;
  }

  get terms(): string | undefined {
    return this.signUpData?.terms;
  }

  get isSso(): boolean {
    return !!this.signUpData?.isSsoEnabled;
  }

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private authService: AuthService,
    private router: Router,
    private translationService: TranslationService,
    private environmentService: EnvironmentService
  ) {
    this.signUpForm = this.formBuilder.group({
      pincode: ['', [Validators.required, pincodeValidator()]],
      firstname: ['', Validators.required],
      lastname: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.pattern(/^\d{6}$/)]]
    });
  }

  async ngOnInit(): Promise<void> {
    // TODO: get language and handle missing token
    const token = this.route.snapshot.paramMap.get('token');

    if (token) {
      try {
        this.signUpData = await this.authService.getSignUpData(token, '');
        this.translationService.setAuthLanguage(this.signUpData.languageId ?? 1045);

        if (this.signUpData.redirectToLogin) {
          this.router.navigate(['/login']);
        }

        this.onPatchFormValue();
      } catch (error) {
        this.router.navigate(['/login']);
      }
    }
  }


  onPatchFormValue(): void {
    const { firstname, lastname, email, mobil } = this.signUpData;
    this.user = { firstname, lastname, email, phone: mobil ?? '' };
    this.companyName.set(this.signUpData.companyName ?? '');
    this.signUpForm.patchValue(this.user);
  }

  async goToNextStep(step: Step): Promise<void> {
    this.isTransitioning = true;

    if (step === Step.PersonalDetails) {
      const pincode = this.signUpForm.get('pincode');

      const model: SignUp = {
        ...this.signUpData,
        pin: this.isSso ? '1234' : pincode?.value,
        pin2: this.isSso ? '1234' : pincode?.value
      };

      // TODO: handle validation error
      await this.authService.onSignUp(model);
    }

    setTimeout(() => {
      this.step.set(step);
      this.isTransitioning = false;
    }, 400);
  }

  updatePincode(pin: string): void {
    if (this.pincodeControl?.errors) {
      this.pincodeControl.setValue('');
      this.pincodeControl.setErrors(null);
      this.pincodeControl.updateValueAndValidity({ onlySelf: true });
    }

    if (pin.length === 4) {
      this.submitPincode(pin);
    }
  }

  submitPincode(pin: string): void {
    if (this.step() === 'pincode') {
      this.signUpForm.get('pincode')?.setValue(pin);

      if (this.signUpForm.controls['pincode'].errors) {
        this.pincodeControl?.updateValueAndValidity({ onlySelf: true });
        this.pincodeControl?.markAsTouched();

        return;
      }

      this.goToNextStep(Step.ConfirmPincode);
    } else if (this.step() === 'confirm-pincode') {
      if (this.signUpForm.get('pincode')?.value === pin) {
        this.goToNextStep(Step.Consent);
      } else {
        this.pincodeNotMatched.set(true);
      }
    }
  }

  goToPincode(): void {
    this.goToNextStep(Step.Pincode);
    this.pincodeNotMatched.set(false);
    this.signUpForm.get('pincode')?.setValue('');
    this.signUpForm.get('pincode')?.markAsUntouched();
  }

  async completeSignup(): Promise<void> {
    // TODO: Handle error validation and auto login
    const phone = this.signUpForm.get('phone')?.value;
    const phoneNumber = phone.replace(/\s+/g, '');

    const model: SignUp = {
      ...this.signUpData,
      email: this.signUpForm.get('email')?.value,
      firstname: this.signUpForm.get('firstname')?.value,
      lastname: this.signUpForm.get('lastname')?.value,
      mobil: phoneNumber
    };

    try {
      const languageCode = this.signUpLanguageCode();
      const response = await this.authService.onConfirmSignUp(model, languageCode);

      if (response.status === HttpStatusCode.Ok) {
        if (this.isSso) {
          this.goToNextStep(Step.SsoLinking);

          return;
        }

        const jwtToken = await lastValueFrom(this.authService.onLogin(model.email, this.signUpForm.get('pincode')?.value));

        this.authService.setCookie(jwtToken, false);
        await this.translationService.refreshTranslationsFromApi(`${this.signUpData.languageId ?? 1045}`);
        this.router.navigate(['/available-surveys']);
      }
    } catch (errorResponse) {
      if (errorResponse instanceof HttpErrorResponse) {
        const errors = errorResponse?.error?.errors;

        if (errors) {
          const firstKey = Object.keys(errors)[0];
          this.signUpValidationError.set(errors[firstKey][0]);
        } else {
          this.signUpValidationError.set('WebApp.v1.Auth.SignUp.Error.SomethingWentWrong');
        }
      }
    }
  }

  continueToSsoPage(): void {
    const ssoUrl = this.environmentService.getEnvironment().ssoUrl;
    const redirectUrl = `${window.location.origin}/login`;

    localStorage.setItem('sso-email', this.signUpForm.get('email')?.value ?? '');
    window.location.href = `${ssoUrl}?redirect=${redirectUrl}`;
  }

  private signUpLanguageCode(): string {
    switch (this.signUpData.languageId) {
      case 1045: return 'en';
      case 1041: return 'da';
      case 1153: return 'se';
      default:   return 'en';
    }
  }
}
